import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Transactions.css';
import { 
  FiSearch, 
  FiEye, 
  FiX, 
  FiMapPin,
  FiClock,
  FiGlobe,
  FiNavigation,
  FiTrendingUp,
  FiActivity,
  FiAlertCircle,
  FiUserCheck,
  FiCreditCard,
  FiArrowUpRight,
  FiArrowDownRight,
  FiRefreshCw,
  FiDollarSign
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';

// ============ LOCATION INTERFACE ============
interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  timestamp: string;
}

// ============ TRANSACTION INTERFACE ============
interface Transaction {
  reference: string;
  amount: number;
  currency: string;
  transaction_type: string;
  status: string;
  description: string;
  completed_at: string;
  direction: string;
  counterparty: string;
  sender_email?: string;
  recipient_email?: string;
  latitude: number | null;
  longitude: number | null;
  location_accuracy: number | null;
  location_timestamp: string | null;
  source: string;
}

interface PaginationData {
  limit: number;
  offset: number;
  total: number;
}

interface WalletSearchResult {
  user_id: number;
  email: string;
  dssn: string;
  name: string;
  first_name: string;
  last_name: string;
  phone: string;
  balance_usd: number;
  balance_lrd: number;
  is_active: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

// ============ HELPER: SAFELY PARSE LOCATION VALUES ============
const safeParseFloat = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

const safeFormatLocation = (value: any): string => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const parsed = parseFloat(value);
  if (isNaN(parsed)) return 'N/A';
  return parsed.toFixed(6);
};

// ============ LOCATION MAP COMPONENT ============
const LocationMap: React.FC<{ location: LocationData }> = ({ location }) => {
  const [mapError, setMapError] = useState(false);

  if (!location) {
    return null;
  }

  const lat = location.latitude;
  const lng = location.longitude;
  
  const bbox = `${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}`;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  const fullMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`;

  return (
    <div className="location-map-container">
      {!mapError ? (
        <iframe
          width="100%"
          height="200"
          style={{ border: 0, borderRadius: '8px' }}
          loading="lazy"
          allowFullScreen
          src={mapUrl}
          title="Transaction Location Map"
          onError={() => setMapError(true)}
        />
      ) : (
        <div className="map-error-fallback">
          <FiMapPin size={32} />
          <p>Map unavailable</p>
          <a href={fullMapUrl} target="_blank" rel="noopener noreferrer">
            Open in OpenStreetMap →
          </a>
        </div>
      )}
      <div className="map-link">
        <a 
          href={fullMapUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View on OpenStreetMap →
        </a>
      </div>
    </div>
  );
};

// ============ SKELETON COMPONENT ============
const SkeletonRow: React.FC = () => (
  <div className="skeleton-row">
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
  </div>
);

const Transactions: React.FC = () => {
  const { token, employee } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
  const [summary, setSummary] = useState({
    total_transactions: 0,
    total_inflow: 0,
    total_outflow: 0
  });
  const [pagination, setPagination] = useState<PaginationData>({
    limit: 20,
    offset: 0,
    total: 0
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResultInfo, setSearchResultInfo] = useState<string | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ============================================================
  // FETCH TRANSACTIONS - SHOW ALL TRANSACTIONS
  // ============================================================
  const fetchTransactions = useCallback(async (options?: { limit?: number; offset?: number; isLoadMore?: boolean }) => {
    const { limit = 20, offset = 0, isLoadMore = false } = options || {};
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    if (!isLoadMore) {
      setLoading(true);
    }
    setError(null);
    setSearchResultInfo(null);
    setIsSearchMode(false);
    
    try {
      const safeLimit = Math.min(limit, 100);
      const params = new URLSearchParams();
      params.append('limit', String(safeLimit));
      params.append('offset', String(offset));
      if (currencyFilter) params.append('currency', currencyFilter);

      const url = `${API_URL}/transactions/all?${params.toString()}`;
      
      console.log('📊 Fetching ALL transactions from:', url);
      
      const response = await axios.get(url, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 30000
      });
      
      console.log('📊 Response data:', response.data);
      
      if (response.data && response.data.success) {
        const txData = response.data.data?.transactions || [];
        console.log('📊 Transactions found:', txData.length);
        
        const sanitizedData = txData.map((tx: any) => ({
          ...tx,
          amount: parseFloat(tx.amount) || 0,
          latitude: safeParseFloat(tx.latitude),
          longitude: safeParseFloat(tx.longitude),
          location_accuracy: safeParseFloat(tx.location_accuracy),
        }));
        
        // Store all transactions for filtering
        setAllTransactions(sanitizedData);
        
        if (isLoadMore) {
          setTransactions(prev => [...prev, ...sanitizedData]);
        } else {
          setTransactions(sanitizedData);
        }
        
        setSummary({
          total_transactions: response.data.data?.summary?.total_transactions || sanitizedData.length,
          total_inflow: response.data.data?.summary?.total_inflow || 0,
          total_outflow: response.data.data?.summary?.total_outflow || 0
        });
        
        if (response.data.data?.pagination) {
          setPagination({
            limit: response.data.data.pagination.limit || safeLimit,
            offset: response.data.data.pagination.offset || offset,
            total: response.data.data.pagination.total || sanitizedData.length
          });
        }
      } else {
        console.log('📊 No transactions or invalid response');
        if (!isLoadMore) {
          setAllTransactions([]);
          setTransactions([]);
          setSummary({ total_transactions: 0, total_inflow: 0, total_outflow: 0 });
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('❌ Failed to fetch transactions:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          setError('The request took too long. Please try again with a smaller limit or specific filters.');
        } else if (error.response?.status === 504) {
          setError('Gateway Timeout: The server took too long to respond. Please try again with fewer transactions or specific filters.');
        } else if (error.response?.status === 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(error.response?.data?.message || error.message || 'Failed to load transactions');
        }
        
        if (!isLoadMore) {
          setAllTransactions([]);
          setTransactions([]);
        }
      }
    } finally {
      if (controller === abortControllerRef.current) {
        if (!isLoadMore) {
          setLoading(false);
        }
        setLoadingMore(false);
        setIsSearching(false);
      }
    }
  }, [token, currencyFilter]);

  // ============================================================
  // INITIAL FETCH
  // ============================================================
  useEffect(() => {
    fetchTransactions({ limit: 20, offset: 0 });
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchTransactions]);

  // ============================================================
  // LOAD MORE TRANSACTIONS
  // ============================================================
  const handleLoadMore = () => {
    if (loadingMore || transactions.length >= pagination.total) return;
    
    const newOffset = pagination.offset + pagination.limit;
    setLoadingMore(true);
    
    fetchTransactions({ 
      limit: pagination.limit, 
      offset: newOffset,
      isLoadMore: true
    });
  };

  // ============================================================
  // SEARCH BY DSSN, EMAIL, OR NAME
  // ============================================================
  const searchByDSSN = async () => {
    const searchTerm = search.trim();
    if (!searchTerm) {
      // Reset to show all transactions
      setTransactions(allTransactions);
      setIsSearchMode(false);
      setSearchResultInfo(null);
      setSummary({
        total_transactions: allTransactions.length,
        total_inflow: allTransactions.filter(t => t.direction === 'received').reduce((sum, t) => sum + t.amount, 0),
        total_outflow: allTransactions.filter(t => t.direction === 'sent').reduce((sum, t) => sum + t.amount, 0)
      });
      return;
    }

    setIsSearching(true);
    setLoading(true);
    setError(null);
    setSearchResultInfo(null);
    
    try {
      // Determine what type of search to perform
      let searchParam = '';
      let searchValue = '';
      
      if (/^[A-Z0-9]{8,20}$/i.test(searchTerm)) {
        searchParam = 'dssn';
        searchValue = searchTerm;
      } else if (searchTerm.includes('@')) {
        searchParam = 'email';
        searchValue = searchTerm;
      } else {
        searchParam = 'name';
        searchValue = searchTerm;
      }
      
      console.log(`📊 Searching wallets by ${searchParam}: ${searchValue}`);
      
      const walletResponse = await axios.get(
        `${API_URL}/wallets/search?${searchParam}=${encodeURIComponent(searchValue)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 10000
        }
      );
      
      console.log('📊 Wallet search response:', walletResponse.data);
      
      if (!walletResponse.data?.success || !walletResponse.data?.data) {
        setTransactions([]);
        setSummary({ total_transactions: 0, total_inflow: 0, total_outflow: 0 });
        setError(`No user found matching: ${searchTerm}`);
        setLoading(false);
        setIsSearching(false);
        return;
      }

      let walletData: WalletSearchResult | WalletSearchResult[] = walletResponse.data.data;
      let userData: WalletSearchResult | null = null;
      
      if (Array.isArray(walletData)) {
        if (walletData.length === 0) {
          setTransactions([]);
          setSummary({ total_transactions: 0, total_inflow: 0, total_outflow: 0 });
          setError(`No user found matching: ${searchTerm}`);
          setLoading(false);
          setIsSearching(false);
          return;
        }
        userData = walletData[0];
      } else {
        userData = walletData;
      }
      
      if (!userData) {
        setTransactions([]);
        setSummary({ total_transactions: 0, total_inflow: 0, total_outflow: 0 });
        setError(`No user found matching: ${searchTerm}`);
        setLoading(false);
        setIsSearching(false);
        return;
      }
      
      const userEmail = userData.email;
      const userDssn = userData.dssn;
      const userName = userData.name || 'User';
      
      console.log(`📊 Found user: ${userName} (${userEmail}) with DSSN: ${userDssn}`);

      // Filter the already loaded transactions by the user's email
      const filteredTx = allTransactions.filter((tx: any) => {
        const senderMatch = tx.sender_email?.toLowerCase() === userEmail.toLowerCase();
        const recipientMatch = tx.recipient_email?.toLowerCase() === userEmail.toLowerCase();
        const counterpartyMatch = tx.counterparty?.toLowerCase().includes(userEmail.toLowerCase());
        return senderMatch || recipientMatch || counterpartyMatch;
      });
      
      console.log(`📊 Filtered transactions: ${filteredTx.length} found for user ${userEmail}`);
      
      setTransactions(filteredTx);
      setIsSearchMode(true);
      setSearchResultInfo(`Found ${filteredTx.length} transactions for: ${userName} (${userEmail})`);
      
      // Update summary for filtered results
      const totalInflow = filteredTx
        .filter(t => t.direction === 'received')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const totalOutflow = filteredTx
        .filter(t => t.direction === 'sent')
        .reduce((sum, t) => sum + t.amount, 0);
      
      setSummary({
        total_transactions: filteredTx.length,
        total_inflow: totalInflow,
        total_outflow: totalOutflow
      });
      
      setPagination({
        limit: 100,
        offset: 0,
        total: filteredTx.length
      });

      if (filteredTx.length === 0) {
        setError(`No transactions found for user: ${userName} (${userEmail})`);
      } else {
        console.log(`✅ Found ${filteredTx.length} transactions for ${userName}`);
      }
    } catch (error: any) {
      console.error('❌ Search failed:', error);
      
      if (error.response?.status === 404) {
        setError(`No user found matching: ${searchTerm}`);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setError('Search request timed out. Please try again.');
      } else if (error.response?.status === 504) {
        setError('Search timed out. Please try with a more specific search term.');
      } else {
        setError(error.response?.data?.message || 'Search failed. Please try again.');
      }
      setTransactions([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchByDSSN();
    }
  };

  const handleReset = () => {
    setSearch('');
    setStatusFilter('');
    setCurrencyFilter('');
    setError(null);
    setSearchResultInfo(null);
    setIsSearchMode(false);
    // Show all transactions again
    setTransactions(allTransactions);
    setSummary({
      total_transactions: allTransactions.length,
      total_inflow: allTransactions.filter(t => t.direction === 'received').reduce((sum, t) => sum + t.amount, 0),
      total_outflow: allTransactions.filter(t => t.direction === 'sent').reduce((sum, t) => sum + t.amount, 0)
    });
    setPagination({
      limit: 20,
      offset: 0,
      total: allTransactions.length
    });
  };

  // ============================================================
  // FILTER TRANSACTIONS (Client-side)
  // ============================================================
  const filteredTransactions = transactions.filter(tx => {
    const searchLower = search.toLowerCase();
    const matchesSearch = 
      tx.reference?.toLowerCase().includes(searchLower) ||
      tx.counterparty?.toLowerCase().includes(searchLower) ||
      tx.description?.toLowerCase().includes(searchLower) ||
      tx.sender_email?.toLowerCase().includes(searchLower) ||
      tx.recipient_email?.toLowerCase().includes(searchLower);
    const matchesStatus = !statusFilter || tx.status === statusFilter;
    const matchesCurrency = !currencyFilter || tx.currency === currencyFilter;
    return matchesSearch && matchesStatus && matchesCurrency;
  });

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      completed: 'active',
      pending: 'pending',
      failed: 'rejected'
    };
    return `status-badge ${statusMap[status] || 'pending'}`;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      transfer: 'Transfer',
      nfc_payment: 'NFC Payment',
      qr_payment: 'QR Payment',
      merchant_payment: 'Merchant Payment',
      merchant_sale: 'Merchant Sale',
      payment: 'Payment',
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      p2p: 'P2P Transfer'
    };
    return labels[type] || type || 'Transfer';
  };

  const getDirectionIcon = (direction: string) => {
    if (direction === 'received') {
      return <FiArrowDownRight className="direction-icon received" />;
    }
    return <FiArrowUpRight className="direction-icon sent" />;
  };

  const getDirectionLabel = (direction: string) => {
    return direction === 'received' ? 'Received' : 'Sent';
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && transactions.length === 0) {
    return (
      <div className="transactions-page">
        <div className="ai-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-overlay"></div>
        </div>
        <div className="transactions-container">
          <div className="page-header glass">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-subtitle"></div>
            </div>
          </div>
          <div className="summary-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="summary-card glass skeleton-summary"></div>
            ))}
          </div>
          <div className="filters-container glass">
            <div className="skeleton-search"></div>
          </div>
          <div className="transactions-table-container glass">
            <div className="skeleton-table">
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <div className="ai-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="transactions-container">
        <div className="page-header glass">
          <div className="header-content">
            <div className="title-section">
              <div className="badge">
                <HiOutlineSparkles />
                <span>Transaction Monitoring</span>
              </div>
              <h1>Transaction Registry</h1>
              <p>Monitor all transactions across the platform</p>
            </div>
            <div className="header-stats">
              <div className="mini-stat">
                <FiTrendingUp />
                <span>Live Monitoring</span>
              </div>
              <div className="mini-stat">
                <FiActivity />
                <span>Real-time</span>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-grid">
          <div className="summary-card glass">
            <div className="summary-icon total">
              <FiDollarSign />
            </div>
            <div className="summary-info">
              <h3>{summary.total_transactions}</h3>
              <p>Total Transactions</p>
            </div>
          </div>
          <div className="summary-card glass">
            <div className="summary-icon inflow">
              <FiArrowDownRight />
            </div>
            <div className="summary-info">
              <h3>${summary.total_inflow.toFixed(2)}</h3>
              <p>Total Inflow</p>
            </div>
          </div>
          <div className="summary-card glass">
            <div className="summary-icon outflow">
              <FiArrowUpRight />
            </div>
            <div className="summary-info">
              <h3>${summary.total_outflow.toFixed(2)}</h3>
              <p>Total Outflow</p>
            </div>
          </div>
          <div className="summary-card glass">
            <div className="summary-icon volume">
              <FiTrendingUp />
            </div>
            <div className="summary-info">
              <h3>{(summary.total_inflow + summary.total_outflow).toFixed(2)}</h3>
              <p>Total Volume</p>
            </div>
          </div>
        </div>

        {searchResultInfo && (
          <div className="search-result-info glass">
            <FiUserCheck size={18} />
            <span>{searchResultInfo}</span>
            <button onClick={handleReset} className="clear-search-btn">
              <FiX size={14} /> Clear
            </button>
          </div>
        )}

        {error && (
          <div className="error-container glass">
            <FiAlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => fetchTransactions({ limit: 20, offset: 0 })} className="retry-btn">
              <FiRefreshCw size={14} /> Retry
            </button>
          </div>
        )}

        <div className="filters-container glass">
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by DSSN, Email, Name, Reference, or Counterparty..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
            />
            <span className="search-hint">↵</span>
          </div>
          <div className="filter-controls">
            <select 
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select 
              className="filter-select"
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
            >
              <option value="">All Currencies</option>
              <option value="USD">USD</option>
              <option value="LRD">LRD</option>
            </select>
            <button onClick={searchByDSSN} className="search-btn primary" disabled={isSearching}>
              <FiSearch /> {isSearching ? 'Searching...' : 'Search'}
            </button>
            <button onClick={handleReset} className="search-btn secondary">
              <FiX /> Reset
            </button>
          </div>
        </div>

        <div className="transactions-table-container glass">
          <div className="table-header">
            <div className="table-title">
              <FiCreditCard />
              <span>Transactions</span>
              <span className="tx-count">{filteredTransactions.length} found</span>
              {pagination.total > 0 && pagination.total > filteredTransactions.length && (
                <span className="tx-total">(Total: {pagination.total})</span>
              )}
              {isSearchMode && (
                <span className="search-mode-badge">🔍 Search Results</span>
              )}
            </div>
            <div className="table-refresh">
              <button onClick={() => fetchTransactions({ limit: 20, offset: 0 })} className="refresh-btn" title="Refresh">
                <FiRefreshCw />
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <span>Loading transactions...</span>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="empty-state">
              <FiAlertCircle className="empty-icon" />
              <h3>No transactions found</h3>
              <p>
                {search 
                  ? `No transactions found for: ${search}`
                  : 'No transactions match your filters'}
              </p>
              {search && (
                <div className="search-hint-text">
                  <p>Try searching by:</p>
                  <ul>
                    <li><strong>DSSN:</strong> e.g., PYGMNL94LR09Z24</li>
                    <li><strong>Email:</strong> e.g., user@example.com</li>
                    <li><strong>Name:</strong> e.g., John Doe</li>
                  </ul>
                  <button onClick={searchByDSSN} className="search-btn primary" style={{ marginTop: '8px' }}>
                    <FiSearch /> Search
                  </button>
                </div>
              )}
              {search && (
                <button onClick={handleReset} className="reset-btn" style={{ marginTop: '12px' }}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Date & Time</th>
                    <th>Type</th>
                    <th>Direction</th>
                    <th>Counterparty</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx) => (
                    <tr key={tx.reference} className="tx-row">
                      <td className="tx-reference">
                        <code>{tx.reference}</code>
                      </td>
                      <td className="tx-date">
                        {tx.completed_at ? new Date(tx.completed_at).toLocaleString() : '—'}
                      </td>
                      <td>
                        <span className="tx-type">
                          {getTypeLabel(tx.transaction_type)}
                          {tx.source === 'merchant' && <span className="tx-source-badge">Merchant</span>}
                        </span>
                      </td>
                      <td>
                        <span className="tx-direction">
                          {getDirectionIcon(tx.direction)}
                          {getDirectionLabel(tx.direction)}
                        </span>
                      </td>
                      <td className="tx-counterparty">
                        {tx.counterparty || '—'}
                      </td>
                      <td className={tx.currency === 'USD' ? 'usd-amount' : 'lrd-amount'}>
                        {tx.currency === 'USD' ? '$' : 'L$'}{tx.amount.toFixed(2)}
                      </td>
                      <td>
                        <span className={getStatusBadge(tx.status)}>
                          {tx.status}
                        </span>
                      </td>
                      <td>
                        {tx.latitude && tx.longitude ? (
                          <span className="location-indicator has-location">
                            <FiMapPin size={14} />
                            <span className="location-tooltip">
                              Click to view
                            </span>
                          </span>
                        ) : (
                          <span className="location-indicator no-location">—</span>
                        )}
                      </td>
                      <td>
                        <button className="action-btn view" onClick={() => setSelectedTx(tx)}>
                          <FiEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {!loading && pagination.total > filteredTransactions.length && filteredTransactions.length > 0 && (
            <div className="table-footer">
              <button 
                onClick={handleLoadMore} 
                className="load-more-btn"
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>Loading more...</>
                ) : (
                  <>Load More ({filteredTransactions.length} of {pagination.total})</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedTx && (
        <div className="modal-overlay" onClick={() => setSelectedTx(null)}>
          <div className="modal-container glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <FiCreditCard />
                <h2>Transaction Details</h2>
              </div>
              <button className="modal-close" onClick={() => setSelectedTx(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="tx-summary glass">
                <div className="tx-amount-large">
                  <span className={`tx-currency ${selectedTx.currency === 'USD' ? 'usd' : 'lrd'}`}>
                    {selectedTx.currency === 'USD' ? '$' : 'L$'}{selectedTx.amount.toFixed(2)}
                  </span>
                  <span className="tx-status-large">
                    <span className={getStatusBadge(selectedTx.status)}>
                      {selectedTx.status}
                    </span>
                  </span>
                </div>
                <div className="tx-meta">
                  <span className="tx-reference-large">{selectedTx.reference}</span>
                  <span className="tx-date-large">
                    <FiClock size={14} />
                    {selectedTx.completed_at ? new Date(selectedTx.completed_at).toLocaleString() : '—'}
                  </span>
                </div>
              </div>

              <div className="tx-details-grid">
                <div className="tx-detail-item">
                  <span className="detail-label">Transaction Type</span>
                  <span className="detail-value">{getTypeLabel(selectedTx.transaction_type)}</span>
                </div>
                <div className="tx-detail-item">
                  <span className="detail-label">Source</span>
                  <span className="detail-value">{selectedTx.source === 'merchant' ? 'Merchant' : 'Wallet'}</span>
                </div>
                <div className="tx-detail-item">
                  <span className="detail-label">Direction</span>
                  <span className="detail-value tx-direction">
                    {getDirectionIcon(selectedTx.direction)}
                    {getDirectionLabel(selectedTx.direction)}
                  </span>
                </div>
                <div className="tx-detail-item">
                  <span className="detail-label">Counterparty</span>
                  <span className="detail-value">{selectedTx.counterparty || '—'}</span>
                </div>
                <div className="tx-detail-item">
                  <span className="detail-label">Currency</span>
                  <span className="detail-value">{selectedTx.currency}</span>
                </div>
                <div className="tx-detail-item full-width">
                  <span className="detail-label">Description</span>
                  <span className="detail-value">{selectedTx.description || '—'}</span>
                </div>
              </div>

              {selectedTx.latitude && selectedTx.longitude ? (
                <div className="tx-location-section glass">
                  <h3>
                    <FiNavigation /> Transaction Location
                    <span className="live-indicator">
                      <span className="live-dot"></span>
                      LOCATION
                    </span>
                  </h3>
                  <div className="info-row">
                    <span className="label">Latitude:</span>
                    <span className="value location-coord">
                      {safeFormatLocation(selectedTx.latitude)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Longitude:</span>
                    <span className="value location-coord">
                      {safeFormatLocation(selectedTx.longitude)}
                    </span>
                  </div>
                  {selectedTx.location_accuracy && (
                    <div className="info-row">
                      <span className="label">Accuracy:</span>
                      <span className="value">
                        {safeFormatLocation(selectedTx.location_accuracy)} meters
                      </span>
                    </div>
                  )}
                  {selectedTx.location_timestamp && (
                    <div className="info-row">
                      <span className="label">
                        <FiClock size={12} /> Captured:
                      </span>
                      <span className="value">
                        {new Date(selectedTx.location_timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <LocationMap location={{
                    latitude: typeof selectedTx.latitude === 'number' ? selectedTx.latitude : parseFloat(String(selectedTx.latitude)),
                    longitude: typeof selectedTx.longitude === 'number' ? selectedTx.longitude : parseFloat(String(selectedTx.longitude)),
                    accuracy: selectedTx.location_accuracy,
                    timestamp: selectedTx.location_timestamp || selectedTx.completed_at
                  }} />
                </div>
              ) : (
                <div className="tx-location-section glass no-location">
                  <h3><FiMapPin /> Transaction Location</h3>
                  <div className="no-location-message">
                    <FiMapPin size={32} className="no-location-icon" />
                    <p>No location data available for this transaction</p>
                    <span className="no-location-subtext">
                      Location was not captured or not available
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setSelectedTx(null)} className="close-modal-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
